import { Link } from 'react-router-dom';

/**
 * Terms of Service — public, no login required. Linked from the Twilio SMS
 * opt-in card and referenced in registration disclosures. Keep this content
 * accurate and up-to-date; Twilio toll-free / A2P reviewers may inspect it.
 */
export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white text-gray-800">
      <header className="border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="text-navy font-bold text-lg">Park City Chiefs</Link>
          <Link to="/" className="text-sm text-navy hover:underline" data-testid="terms-back-home">Back to site</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6" data-testid="terms-page">
        <h1 className="text-3xl font-bold text-navy">Terms of Service</h1>
        <p className="text-sm text-gray-500">Last updated: February 2026</p>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">1. About us</h2>
          <p>
            Park City Chiefs is a youth sports organization. These Terms of Service govern your
            use of our website, member portal, and messaging channels. By creating an account
            or using our services you agree to these Terms.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">2. Accounts</h2>
          <p>
            You are responsible for keeping your login credentials secure. You must provide
            accurate information about yourself and any players you are the parent or legal
            guardian of.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">3. SMS text messaging</h2>
          <p>
            If you opt in to SMS alerts, you agree to receive recurring informational text
            messages from Park City Chiefs about practices, games, events, schedule changes,
            cancellations, volunteer opportunities, and organization updates.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Message frequency varies.</li>
            <li>Message and data rates may apply. Check with your mobile carrier.</li>
            <li>Reply <strong>STOP</strong> at any time to opt out. Reply <strong>HELP</strong> for help.</li>
            <li>Consent to SMS is optional and is not a condition of registration or participation.</li>
            <li>Supported carriers include AT&amp;T, T-Mobile, Verizon Wireless, and most U.S. mobile carriers. Carriers are not liable for delayed or undelivered messages.</li>
          </ul>
          <p>
            Your consent record and phone number are stored securely by Park City Chiefs and
            our SMS delivery provider. See the <Link to="/privacy-policy" className="text-navy underline">Privacy Policy</Link> for details.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">4. Payments and refunds</h2>
          <p>
            Registration fees, uniforms, and other purchases are handled through our payment
            provider. Refund policies are described at checkout and in your account.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">5. Prohibited use</h2>
          <p>
            You may not use our site to harass others, violate laws, or attempt to access data
            you are not authorized to view. We may suspend accounts that violate these Terms.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">6. Changes</h2>
          <p>
            We may update these Terms from time to time. We will post the updated version on
            this page and update the &quot;Last updated&quot; date above.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-navy">7. Contact</h2>
          <p>
            Questions? Email us at <a href="mailto:info@parkcitychiefs.org" className="text-navy underline">info@parkcitychiefs.org</a>.
          </p>
        </section>
      </main>
    </div>
  );
}
